/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import { ListItem, OrderedList } from '@getgo/chameleon-chakra-ui';
import React, { useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SessionContext } from '../../../context/SessionContext';
import {
  buildMobileJoinScheme,
  getAndroidAppUrl,
  getIosAppUrl,
} from '../../../services/UrlService';
import { isAndroid, isIos } from '../../../services/ClientService';
import { getHumanReadableMeetingId } from '../../../lib/meetingIdFormatter';
import { FontWeight, Opacity, Spacing } from '../../../themes/GoToTheme';
import { GoToHeadingText } from '../../JoinPages/GoToPages/Components/Texts/GoToHeadingText';
import { navigateTo } from '../../../services/NavigatorService';
import { GoToLink } from '../../JoinPages/GoToPages/Components/Texts/GoToLink';
import {
  useSendTelemetry,
  TelemetryComponent,
  TelemetryAction,
  TelemetryState,
} from '../../../hooks/useSendTelemetry';
import { GoToText } from '../../Text/GoToText';

const messages = defineMessages({
  heading: {
    id: 'goto.mobile.heading',
  },
  install: {
    id: 'goto.mobile.installCaption',
  },
  installLinkText: {
    id: 'goto.mobile.installLink',
  },
  join: {
    id: 'goto.mobile.joinCaption',
  },
  joinLinkText: {
    id: 'goto.mobile.joinLink',
  },
  id: {
    id: 'goto.mobile.id',
  },
});

export const GoToMobilePage: React.FC = () => {
  const { meetingId, sessionTrackingId, product } = useContext(SessionContext);
  const { formatMessage: f } = useIntl();
  const sendTelemetry = useSendTelemetry();
  const mobileLaunchScheme = buildMobileJoinScheme(
    meetingId,
    sessionTrackingId
  );
  const storeUrl = isAndroid
    ? getAndroidAppUrl(product)
    : getIosAppUrl(product);
  useEffect(() => {
    if (isIos) {
      sendTelemetry(
        TelemetryComponent.Ios,
        TelemetryAction.Onload,
        TelemetryState.SecondManual
      );
    } else if (isAndroid) {
      sendTelemetry(
        TelemetryComponent.Android,
        TelemetryAction.Onload,
        TelemetryState.SecondAutomatic
      );
      navigateTo(mobileLaunchScheme);
    }
  });

  return (
    <>
      <GoToHeadingText marginBottom={Spacing.CoreLayout3}>
        {f(messages.heading)}
      </GoToHeadingText>
      <OrderedList spacing={Spacing.CoreLayout2}>
        <ListItem>
          <GoToText>{f(messages.install)}</GoToText>
          <GoToLink fontWeight={FontWeight.Medium} href={storeUrl}>
            {f(messages.installLinkText)}
          </GoToLink>
        </ListItem>
        <ListItem>
          <GoToText>{f(messages.join)}</GoToText>
          <GoToLink
            fontWeight={FontWeight.Medium}
            href={mobileLaunchScheme}
            onClick={() => {
              if (isIos) {
                sendTelemetry(
                  TelemetryComponent.Ios,
                  TelemetryAction.Join,
                  TelemetryState.First
                );
              } else if (isAndroid) {
                sendTelemetry(
                  TelemetryComponent.Android,
                  TelemetryAction.Join,
                  TelemetryState.First
                );
              }
            }}
          >
            {f(messages.joinLinkText)}
          </GoToLink>
        </ListItem>
      </OrderedList>
      <GoToText marginTop={Spacing.CoreLayout3} opacity={Opacity.Medium}>
        {meetingId &&
          `${f(messages.id)} ${getHumanReadableMeetingId(meetingId)}`}
      </GoToText>
    </>
  );
};
